/**
 * Push Notifications client helper.
 *
 * Flow:
 *  1. `getPushState()` — sprawdź czy supported / permission state / subscribed
 *  2. `enablePush()` — request permission → subscribe → POST /api/push/subscribe
 *  3. `disablePush()` — unsubscribe + POST /api/push/unsubscribe
 *  4. `sendTestPush()` — wywołaj /api/push/test (debug button)
 */
import axios from 'axios';
import { API } from '@/utils/api';
import { clientHeaders } from '@/utils/marketplaceEvents';

const SW_PATH = '/push-sw.js';
const VAPID_PUBLIC = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

async function getRegistration() {
  if (!isPushSupported()) return null;
  let reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) {
    reg = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
    await navigator.serviceWorker.ready;
  }
  return reg;
}

export async function getPushState() {
  if (!isPushSupported()) {
    return { supported: false, permission: 'unsupported', subscribed: false };
  }
  const permission = Notification.permission; // 'default' | 'granted' | 'denied'
  if (permission !== 'granted') {
    return { supported: true, permission, subscribed: false };
  }
  try {
    const reg = await getRegistration();
    if (!reg) return { supported: true, permission, subscribed: false };
    const sub = await reg.pushManager.getSubscription();
    return { supported: true, permission, subscribed: !!sub };
  } catch {
    return { supported: true, permission, subscribed: false };
  }
}

export async function enablePush() {
  if (!isPushSupported()) throw new Error('UNSUPPORTED');
  if (!VAPID_PUBLIC) throw new Error('VAPID_NOT_CONFIGURED');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied' ? 'DENIED' : 'DISMISSED');
  }

  const reg = await getRegistration();
  if (!reg) throw new Error('REGISTRATION_FAILED');

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
    });
  }
  // POST do backendu
  await axios.post(`${API}/push/subscribe`, sub.toJSON(), { headers: clientHeaders() });
  return { ok: true };
}

export async function disablePush() {
  if (!isPushSupported()) return { ok: true };
  const reg = await getRegistration();
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }, { headers: clientHeaders() });
    } catch { /* best-effort */ }
    await sub.unsubscribe();
  }
  return { ok: true };
}

export async function sendTestPush() {
  await axios.post(`${API}/push/test`, {}, { headers: clientHeaders() });
}
